import React, { useState, useEffect } from 'react';
import BlurUpImage from '../components/common/BlurUpImage';

const EarnCryptoPage = () => {
  const [faqOpen, setFaqOpen] = useState(null);
  const [amount, setAmount] = useState(200000);
  const [term, setTerm] = useState(12);
  const [cryptoData, setCryptoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [cryptoPrices, setCryptoPrices] = useState({});

  const faqs = [
    {
      question: "How do I earn interest?",
      answer: "Simply deposit your crypto or fiat assets into your Blockfirst Capital account and start earning daily interest. No minimums or lock-ups required."
    },
    {
      question: "How do I deposit crypto into my account?",
      answer: "You can deposit crypto by sending it to your Blockfirst Capital wallet address, or purchase directly through our platform using fiat currency."
    },
    {
      question: "How is the interest calculated?",
      answer: "Interest is calculated daily based on your account balance and the current APY rates. Your earnings are compounded daily for maximum growth potential."
    },
    {
      question: "What are the withdrawal fees?",
      answer: "There are no withdrawal fees. You can withdraw your funds anytime without any penalties or charges."
    }
  ];

  const cryptoRates = [
    { symbol: 'BTC', name: 'Bitcoin', rate: '6%', icon: '₿' },
    { symbol: 'ETH', name: 'Ethereum', rate: '7%', icon: 'Ξ' },
    { symbol: 'USDC', name: 'USD Coin', rate: '11%', icon: '💲' },
    { symbol: 'DOT', name: 'Polkadot', rate: '17%', icon: '🔴' },
    { symbol: 'LTC', name: 'Litecoin', rate: '7%', icon: 'Ł' },
    { symbol: 'BCH', name: 'Bitcoin Cash', rate: '7%', icon: '₿' }
  ];

  const benefits = [
    {
      icon: '💰',
      title: 'Daily Payouts',
      description: 'Interest is paid out daily, directly to your Blockfirst Capital account.'
    },
    {
      icon: '📈',
      title: 'High-Yield Interest',
      description: 'Earn up to 17% annual interest on your assets.'
    },
    {
      icon: '🔓',
      title: 'Flexible Terms',
      description: 'Withdraw your funds anytime, with no lock-ins or fees.'
    },
    {
      icon: '🛡️',
      title: 'Insured Assets',
      description: 'Your assets are protected with industry-leading security measures.'
    },
    {
      icon: '💎',
      title: 'Compounding Interest',
      description: 'Your interest earns interest, maximizing your returns.'
    }
  ];

  const calculateEarnings = () => {
    const monthlyRate = 0.16 / 12;
    const totalAmount = amount * Math.pow(1 + monthlyRate, term);
    return Math.round(totalAmount - amount);
  };

  const totalBalance = amount + calculateEarnings();

  const refreshCryptoData = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,ripple,cardano,solana,polkadot,usd-coin,avalanche-2,matic-network,chainlink&vs_currencies=usd&include_24hr_change=true');
      const data = await response.json();
      
      // Store prices for live data
      setCryptoPrices(data);
      
      const liveCryptoData = [
        { symbol: 'BTC', name: 'Bitcoin', price: `$${data.bitcoin?.usd?.toLocaleString() || '66,000'}`, change: `${data.bitcoin?.usd_24h_change ? (data.bitcoin.usd_24h_change > 0 ? '+' : '') + data.bitcoin.usd_24h_change.toFixed(2) : '0.50'}%`, icon: '₿', color: 'text-orange-500', bgColor: 'bg-orange-50', coinId: 'bitcoin' },
        { symbol: 'ETH', name: 'Ethereum', price: `$${data.ethereum?.usd?.toLocaleString() || '3,400'}`, change: `${data.ethereum?.usd_24h_change ? (data.ethereum.usd_24h_change > 0 ? '+' : '') + data.ethereum.usd_24h_change.toFixed(2) : '0.25'}%`, icon: 'Ξ', color: 'text-blue-500', bgColor: 'bg-blue-50', coinId: 'ethereum' },
        { symbol: 'XRP', name: 'XRP', price: `$${data.ripple?.usd?.toFixed(4) || '0.5000'}`, change: `${data.ripple?.usd_24h_change ? (data.ripple.usd_24h_change > 0 ? '+' : '') + data.ripple.usd_24h_change.toFixed(2) : '-0.15'}%`, icon: '✕', color: 'text-black', bgColor: 'bg-gray-50', coinId: 'ripple' },
        { symbol: 'ADA', name: 'Cardano', price: `$${data.cardano?.usd?.toFixed(4) || '0.4500'}`, change: `${data.cardano?.usd_24h_change ? (data.cardano.usd_24h_change > 0 ? '+' : '') + data.cardano.usd_24h_change.toFixed(2) : '0.30'}%`, icon: '₳', color: 'text-blue-600', bgColor: 'bg-blue-50', coinId: 'cardano' },
        { symbol: 'SOL', name: 'Solana', price: `$${data.solana?.usd?.toLocaleString() || '150'}`, change: `${data.solana?.usd_24h_change ? (data.solana.usd_24h_change > 0 ? '+' : '') + data.solana.usd_24h_change.toFixed(2) : '0.40'}%`, icon: '◎', color: 'text-purple-500', bgColor: 'bg-purple-50', coinId: 'solana' },
        { symbol: 'DOT', name: 'Polkadot', price: `$${data.polkadot?.usd?.toFixed(2) || '7.20'}`, change: `${data.polkadot?.usd_24h_change ? (data.polkadot.usd_24h_change > 0 ? '+' : '') + data.polkadot.usd_24h_change.toFixed(2) : '0.20'}%`, icon: '●', color: 'text-pink-500', bgColor: 'bg-pink-50', coinId: 'polkadot' },
        { symbol: 'USDC', name: 'USD Coin', price: `$${data['usd-coin']?.usd?.toFixed(2) || '1.00'}`, change: `${data['usd-coin']?.usd_24h_change ? (data['usd-coin'].usd_24h_change > 0 ? '+' : '') + data['usd-coin'].usd_24h_change.toFixed(2) : '0.00'}%`, icon: '💲', color: 'text-amber-600', bgColor: 'bg-amber-60', coinId: 'usd-coin' },
        { symbol: 'AVAX', name: 'Avalanche', price: `$${data['avalanche-2']?.usd?.toFixed(2) || '35.00'}`, change: `${data['avalanche-2']?.usd_24h_change ? (data['avalanche-2'].usd_24h_change > 0 ? '+' : '') + data['avalanche-2'].usd_24h_change.toFixed(2) : '0.60'}%`, icon: '❄', color: 'text-red-500', bgColor: 'bg-red-50', coinId: 'avalanche-2' },
        { symbol: 'MATIC', name: 'Polygon', price: `$${data['matic-network']?.usd?.toFixed(4) || '0.8500'}`, change: `${data['matic-network']?.usd_24h_change ? (data['matic-network'].usd_24h_change > 0 ? '+' : '') + data['matic-network'].usd_24h_change.toFixed(2) : '0.35'}%`, icon: '🔷', color: 'text-purple-600', bgColor: 'bg-purple-50', coinId: 'matic-network' },
        { symbol: 'LINK', name: 'Chainlink', price: `$${data.chainlink?.usd?.toFixed(2) || '18.50'}`, change: `${data.chainlink?.usd_24h_change ? (data.chainlink.usd_24h_change > 0 ? '+' : '') + data.chainlink.usd_24h_change.toFixed(2) : '0.45'}%`, icon: '🔗', color: 'text-blue-400', bgColor: 'bg-blue-50', coinId: 'chainlink' }
      ];
      
      setCryptoData(liveCryptoData);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Error refreshing crypto data:', error);
      setLoading(false);
    }
  };

  // Load crypto data once on mount
  useEffect(() => {
    refreshCryptoData();
  }, []);

  return (
    <div className="min-h-screen bg-white">
        {/* Live Price Ticker */}
        <section className="bg-gray-900 text-white py-4">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between overflow-hidden">
              <div className="flex items-center gap-8 animate-scroll">
                {cryptoData.slice(0, 6).map((crypto, index) => (
                  <div key={index} className="flex items-center gap-3 whitespace-nowrap">
                    <span className="text-lg">{crypto.icon}</span>
                    <span className="font-medium">{crypto.symbol}</span>
                    <span className="text-amber-600">{crypto.price}</span>
                    <span className={`text-sm ${crypto.change.startsWith('+') ? 'text-amber-600' : 'text-red-400'}`}>
                      {crypto.change}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-amber-50 py-20 lg:py-32">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Savings that grow every day
                </h1>
                <p className="text-xl text-gray-600 max-w-lg">
                  Earn high-yield interest on your crypto with daily compounding and no lock-ups. Start building wealth with Blockfirst Capital.
                </p>
                <div className="space-y-4">
                  <button className="bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-all duration-300 transform hover:scale-105 flex items-center gap-2">
                    Start Earning
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                  <p className="text-sm text-gray-500">Already a Blockfirst Capital user? <a href="#" className="text-amber-600 hover:text-amber-600">Log in</a></p>
                </div>
              </div>
              <div className="relative">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-amber-00">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center text-yellow-800 font-bold">₿</div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">You received 0.00000000 BTC</div>
                        <div className="text-xs text-gray-500">Daily interest payout</div>
                      </div>
                      <div className="w-5 h-5 bg-amber-600 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="w-8 h-8 bg-blue-400 rounded-full flex items-center justify-center text-blue-800 font-bold">Ξ</div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">You received 0.00000000 ETH</div>
                        <div className="text-xs text-gray-500">Daily interest payout</div>
                      </div>
                      <div className="w-5 h-5 bg-amber-600 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                    <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c-.28.28-.732.28-1.012 0L5.172 15H4a2 2 0 01-2-2V5a2 2 0 012-2h7a2 2 0 012 2v4z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                The benefits of Blockfirst Capital Savings
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Earn daily interest on your crypto and fiat, paid out daily, with no minimums or maximums.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center p-6 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                  <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <button className="bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-all duration-300 transform hover:scale-105 flex items-center gap-2 mx-auto">
                Learn More
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </div>
        </section>

        {/* Crypto Rates Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Annual rates that drive your long-term success
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Competitive interest rates across a wide range of cryptocurrencies and stablecoins.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {cryptoRates.map((crypto, index) => (
                <div key={index} className="bg-white p-6 rounded-xl text-center hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                  <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center text-2xl mx-auto mb-3">
                    {crypto.icon}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{crypto.symbol}</h3>
                  <p className="text-sm text-gray-600 mb-2">{crypto.name}</p>
                  <div className="text-2xl font-bold text-amber-600">up to {crypto.rate}</div>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <a href="#" className="text-amber-600 hover:text-amber-600 font-semibold">See all rates →</a>
            </div>
          </div>
        </section>

        {/* Live Markets Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Live market prices
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Real-time cryptocurrency prices and 24-hour changes. Stay informed with live market data.
              </p>
              <div className="flex items-center justify-center gap-4 mt-4">
                <button
                  onClick={refreshCryptoData}
                  disabled={loading}
                  className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-all duration-300 flex items-center gap-2 disabled:opacity-50"
                >
                  <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {loading ? 'Updating...' : 'Refresh'}
                </button>
                {lastUpdated && (
                  <span className="text-sm text-gray-500">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </span>
                )}
              </div>
            </div>
            
            {loading ? (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-00"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                {cryptoData.slice(0, 10).map((crypto, index) => (
                  <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-10 h-10 ${crypto.bgColor} rounded-full flex items-center justify-center text-xl`}>
                        <span className={crypto.color}>{crypto.icon}</span>
                      </div>
                      <div className={`text-sm font-medium ${crypto.change.startsWith('+') ? 'text-amber-600' : 'text-red-600'}`}>
                        {crypto.change}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-gray-900">{crypto.symbol}</h3>
                      <p className="text-sm text-gray-600">{crypto.name}</p>
                      <div className="text-lg font-bold text-gray-900">{crypto.price}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="text-center mt-12">
              <button className="bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-all duration-300 transform hover:scale-105 flex items-center gap-2 mx-auto">
                View All Markets
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </div>
        </section>

        {/* Earnings Calculator Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Estimate your earnings
                </h2>
                <p className="text-xl text-gray-600">
                  Use our calculator to see how much you can earn with Blockfirst Capital's interest-bearing account.
                </p>
              </div>
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-8">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                    <div className="text-3xl font-bold text-gray-900 mb-2">
                      ${amount.toLocaleString()}
                    </div>
                    <input
                      type="range"
                      min="1000"
                      max="1000000"
                      step="1000"
                      value={amount}
                      onChange={(e) => setAmount(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Term (months)</label>
                    <div className="text-3xl font-bold text-gray-900 mb-2">
                      {term} months
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="60"
                      step="1"
                      value={term}
                      onChange={(e) => setTerm(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>
                  <div className="bg-amber-60 p-6 rounded-xl border border-amber-00">
                    <div className="text-sm text-gray-600 mb-2">Estimated earnings at 16% APY</div>
                    <div className="text-3xl font-bold text-amber-600">
                      ${calculateEarnings().toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500">Total balance: ${totalBalance.toLocaleString()}</div>
                  </div>
                </div>
                <div className="relative">
                  <div className="bg-gradient-to-br from-amber-50 to-blue-50 p-8 rounded-2xl border border-amber-00">
                    <div className="h-64 bg-white rounded-lg p-4">
                      <div className="h-full bg-gradient-to-t from-amber-200 to-blue-200 rounded-lg relative">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-700 mb-2">Growth Chart</div>
                            <div className="text-sm text-gray-600">Your investment growth over time</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-4">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-amber-600 rounded"></div>
                        <span className="text-sm text-gray-600">Initial Deposit</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-blue-500 rounded"></div>
                        <span className="text-sm text-gray-600">Interest Earned</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-center mt-12">
                <button className="bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-all duration-300 transform hover:scale-105 flex items-center gap-2 mx-auto">
                  Start Earning
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Wealth Solutions Section */}
        <section className="py-20 bg-gray-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-800 to-amber-900 opacity-90"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-8">
                Wealth solutions for private and corporate clients
              </h2>
              <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
                Access bespoke services for your unique needs, including dedicated account managers, OTC trading, and more.
              </p>
              <div className="grid md:grid-cols-2 gap-12 text-left">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-amber-600 rounded-full flex-shrink-0 mt-1"></div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Bespoke services for your unique needs</h3>
                      <p className="text-gray-300">Customized investment strategies tailored to your financial goals and risk tolerance.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-amber-600 rounded-full flex-shrink-0 mt-1"></div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Dedicated account managers</h3>
                      <p className="text-gray-300">Personal relationship managers providing expert guidance and portfolio oversight.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-amber-600 rounded-full flex-shrink-0 mt-1"></div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">OTC trading and execution</h3>
                      <p className="text-gray-300">Large-scale trades executed with minimal market impact and optimal pricing.</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-amber-600 rounded-full flex-shrink-0 mt-1"></div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Build a digital asset treasury</h3>
                      <p className="text-gray-300">Corporate treasury solutions for managing digital assets and crypto investments.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-amber-600 rounded-full flex-shrink-0 mt-1"></div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Customized lending solutions</h3>
                      <p className="text-gray-300">Flexible lending options using your crypto assets as collateral.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-amber-600 rounded-full flex-shrink-0 mt-1"></div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Institutional-grade security</h3>
                      <p className="text-gray-300">Enterprise-level security protocols and compliance standards.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Outstanding
                </h2>
                <blockquote className="text-xl text-gray-600 italic mb-8">
                  "Blockfirst Capital has transformed how I think about crypto investments. The daily compounding and flexible terms make it the perfect platform for building long-term wealth."
                </blockquote>
                <div className="text-center">
                  <div className="font-semibold text-gray-900">John Doe</div>
                  <div className="text-gray-500">Blockfirst Capital User</div>
                </div>
              </div>
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="relative">
                  <BlurUpImage
                    src="/src/assets/trading/retirement.webp"
                    alt="Business professional"
                    className="w-full h-96 object-cover rounded-2xl"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-20 rounded-2xl flex items-center justify-center">
                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-10 h-10 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <h3 className="text-3xl font-bold text-gray-900">
                    Optimize your portfolio
                  </h3>
                  <p className="text-xl text-gray-600">
                    Blockfirst Capital offers a comprehensive suite of products and services designed to help you maximize your investment potential and achieve your financial goals.
                  </p>
                  <button className="bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-all duration-300 transform hover:scale-105 flex items-center gap-2">
                    Explore Products
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Security Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Strong fundamentals support your assets
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Blockfirst Capital's robust financial model and risk management framework ensure the safety and security of your assets.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-amber-600 mb-2">$371+ billion</div>
                <div className="text-gray-600">Assets Under Management</div>
              </div>
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-amber-600 mb-2">$11+ billion</div>
                <div className="text-gray-600">Insurance Coverage</div>
              </div>
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-amber-600 mb-2">$1.2+ billion</div>
                <div className="text-gray-600">Total Loans Issued</div>
              </div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-700 mb-6">Featured in</div>
              <div className="flex justify-center items-center gap-8 flex-wrap">
                <div className="text-2xl font-bold text-gray-400">REUTERS</div>
                <div className="text-2xl font-bold text-gray-400">BLOOMBERG</div>
                <div className="text-2xl font-bold text-gray-4">FORTUNE</div>
                <div className="text-2xl font-bold text-gray-400">MSCI</div>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile App Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-6">
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                    Simplify your experience with our secure tracker
                  </h2>
                  <p className="text-xl text-gray-600">
                    Track your assets, earnings, and transactions in real-time using our mobile app. Stay informed and in control of your investments.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Instant Notifications</h3>
                        <p className="text-gray-600">Stay informed with real-time alerts on your account activity.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                          <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Detailed Analytics</h3>
                        <p className="text-gray-600">Gain insights into your portfolio performance with comprehensive data.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Bank-Grade Security</h3>
                        <p className="text-gray-600">Your assets are protected with industry-leading security measures.</p>
                      </div>
                    </div>
                  </div>
                  <button className="bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-all duration-300 transform hover:scale-105 flex items-center gap-2">
                    Download App
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                </div>
                <div className="relative">
                  <BlurUpImage
                    src="/src/assets/trading/apptrading.png"
                    alt="Mobile app interface"
                    className="w-full max-w-sm mx-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Frequently asked questions
                </h2>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-white rounded-lg border border-gray-200">
                    <button
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                      onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                    >
                      <span className="font-semibold text-gray-900">{faq.question}</span>
                      <svg
                        className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${
                          faqOpen === index ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {faqOpen === index && (
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gray-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to start earning?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Join thousands of investors who are already earning daily interest on their crypto assets with Blockfirst Capital.
            </p>
            <a href="https://web3.blockfirstcapital.com/" target="_blank" rel="noopener noreferrer" className="bg-amber-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-600 transition-all duration-300 transform hover:scale-105 flex items-center gap-2 mx-auto inline-block">
              Get Started Now
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </section>
    </div>
  );
};

export default EarnCryptoPage;
